import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import BrandLogo from "../components/BrandLogo";
import { ArrowLeft, TrendDown, ChartLineUp, ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function BenchmarkDetail() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        api.get(`/intelligence/public-benchmarks/${slug}`)
            .then((r) => {
                setData(r.data);
                const display = r.data.creditor;
                document.title = `${display} settlement % — debt negotiation benchmarks | LienLift Direct`;
                const setMeta = (name, content) => {
                    let m = document.querySelector(`meta[name="${name}"]`);
                    if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
                    m.setAttribute("content", content);
                };
                setMeta("description",
                    `Average settlement on ${display} debts: ${r.data.avg_settlement_pct}% of balance owed. ` +
                    `Based on ${r.data.settled_count} verified settlements via LienLift Direct.`);
                setMeta("robots", "index,follow");
            })
            .catch((e) => {
                if (e?.response?.status === 404) setNotFound(true);
                else toast.error(formatApiError(e));
            })
            .finally(() => setLoading(false));
    }, [slug]);

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-sans">Loading…</div>;

    if (notFound || !data) {
        return (
            <div className="min-h-screen bg-white font-sans flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center px-6">
                    <Card className="max-w-md p-8 text-center">
                        <ChartBar size={32} weight="duotone" className="mx-auto text-slate-400" />
                        <h1 className="font-serif text-2xl mt-4">Not enough data yet</h1>
                        <p className="text-sm text-slate-600 mt-3">
                            We don't have enough verified settlements for this creditor to publish a benchmark.
                        </p>
                        <Link to="/benchmarks">
                            <Button variant="outline" className="mt-5"><ArrowLeft size={14} className="mr-1.5" /> All benchmarks</Button>
                        </Link>
                    </Card>
                </main>
            </div>
        );
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Dataset",
        "name": `${data.creditor} settlement benchmark`,
        "description": `Aggregated debt settlement performance for ${data.creditor}.`,
        "creator": { "@type": "Organization", "name": "LienLift Direct" },
        "variableMeasured": [
            { "@type": "PropertyValue", "name": "Average settlement %", "value": data.avg_settlement_pct },
            { "@type": "PropertyValue", "name": "Median settlement %", "value": data.median_settlement_pct },
            { "@type": "PropertyValue", "name": "Settled cases", "value": data.settled_count },
        ],
    };

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLd)}} />
            <Header />

            <main className="max-w-[1100px] mx-auto px-6 py-16" data-testid="benchmark-detail">
                <Link to="/benchmarks" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
                    <ArrowLeft size={14} /> All benchmarks
                </Link>

                <p className="overline mt-8">Per-creditor benchmark</p>
                <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-tight tracking-tight">
                    {data.creditor}<span className="text-[#2563EB]">.</span>
                </h1>
                <p className="mt-5 text-lg text-slate-700 max-w-2xl">
                    Based on <strong>{data.settled_count}</strong> verified settlements, the average debt owed to{" "}
                    {data.creditor} resolves for <strong className="text-[#0F1E3D]">{data.avg_settlement_pct}%</strong> of the balance.
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden mt-10">
                    <Stat label="Avg accepted" value={`${data.avg_settlement_pct}%`} tint="navy" />
                    <Stat label="Median" value={`${data.median_settlement_pct}%`} tint="blue" />
                    <Stat label="Best deal" value={`${data.best_settlement_pct}%`} tint="green" />
                    <Stat label="Worst deal" value={`${data.worst_settlement_pct}%`} tint="brown" />
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
                    <Stat label="Settled cases" value={data.settled_count} />
                    <Stat label="Total owed" value={`$${data.total_owed.toLocaleString()}`} />
                    <Stat label="Total saved" value={`$${data.total_savings.toLocaleString()}`} tint="green" />
                </div>

                {/* Case-type breakdown */}
                {Object.keys(data.by_case_type || {}).length > 0 && (
                    <Card className="p-6 mt-10">
                        <p className="overline">Case type mix</p>
                        <div className="flex flex-wrap gap-3 mt-3">
                            {Object.entries(data.by_case_type).map(([t, n]) => (
                                <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-sm">
                                    <span className="font-medium capitalize">{t}</span>
                                    <span className="font-mono text-xs text-slate-500">×{n}</span>
                                </span>
                            ))}
                        </div>
                    </Card>
                )}

                {/* What this means + CTA */}
                <Card className="p-8 mt-12 bg-[#0F1E3D] text-white border-0">
                    <p className="overline !text-white/60">What this means for you</p>
                    <h2 className="font-serif text-3xl mt-2 leading-tight">
                        If you owe {data.creditor}, you can reasonably target ~{Math.round(data.avg_settlement_pct)}%.
                    </h2>
                    <p className="mt-4 text-white/80 max-w-2xl">
                        Walk into the call with the average + the best-deal number as your anchor.
                        LienLift Direct drafts the validation, settlement and pay-for-deletion letters at your direction.
                        You sign, you send, you settle.
                    </p>
                    <div className="mt-6 flex gap-3 flex-wrap">
                        <Link to="/register">
                            <Button className="bg-white text-[#0F1E3D] hover:bg-white/90 h-11 px-6">Open a free case →</Button>
                        </Link>
                        <Link to="/benchmarks">
                            <Button variant="ghost" className="text-white hover:bg-white/10 h-11 px-6">See all creditors</Button>
                        </Link>
                    </div>
                </Card>

                <section className="mt-16 text-xs text-slate-500 max-w-2xl">
                    <p>
                        Methodology: each row is the unweighted average of verified LienLift Direct settlements for this creditor.
                        Past settlements do not predict your outcome. LienLift Direct is a non-legal information &amp; document service, not a law firm.
                    </p>
                </section>
            </main>
        </div>
    );
}

function Header() {
    return (
        <header className="border-b border-slate-200">
            <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <BrandLogo height={36} />
                </Link>
                <Link to="/register">
                    <Button className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-9">Open a free case</Button>
                </Link>
            </div>
        </header>
    );
}

function Stat({ label, value, tint }) {
    const tones = {
        navy: { bg: "bg-[#0F1E3D]", text: "text-white", lab: "text-white/60" },
        blue: { bg: "bg-blue-50", text: "text-blue-900", lab: "text-blue-700" },
        green: { bg: "bg-emerald-50", text: "text-emerald-900", lab: "text-emerald-700" },
        brown: { bg: "bg-amber-50", text: "text-amber-900", lab: "text-amber-800" },
    };
    const t = tones[tint] || { bg: "bg-white", text: "text-slate-900", lab: "" };
    return (
        <div className={`p-6 ${t.bg}`}>
            <p className={`overline ${t.lab}`}>{label}</p>
            <p className={`font-mono text-3xl mt-2 font-semibold ${t.text}`}>{value}</p>
        </div>
    );
}
